/**
 * Authenticator-app (TOTP) enrollment panel — renders the QR code, the manual
 * secret fallback, and a confirmation code field. Presentational: the parent
 * owns the API call and passes the secret + provisioning URI in.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Copy, Loader2, Smartphone } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

interface TotpEnrollPanelProps {
  provisioningUri: string;
  secret: string;
  onConfirm: (code: string) => void;
  isConfirming: boolean;
  error?: string | null;
}

export function TotpEnrollPanel({
  provisioningUri,
  secret,
  onConfirm,
  isConfirming,
  error,
}: TotpEnrollPanelProps) {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  const handleCopySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length >= 6) {
      onConfirm(code.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Smartphone className="h-5 w-5 text-primary" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold">{t('mfa.totpSetup.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('mfa.totpSetup.description')}</p>
        </div>
      </div>

      <div className="flex justify-center rounded-lg border border-border bg-card p-4">
        <QRCodeSVG
          value={provisioningUri}
          size={176}
          marginSize={2}
          title={t('mfa.totpSetup.qrCodeAlt')}
        />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">{t('mfa.totpSetup.manualEntry')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded-md bg-muted/60 px-3 py-2 font-mono text-sm tracking-wider">
            {secret}
          </code>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopySecret}
            aria-label={t('mfa.totpSetup.copySecret')}
          >
            {secretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mfa-totp-code">{t('mfa.totpSetup.codeLabel')}</Label>
        <Input
          id="mfa-totp-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          className="text-center font-mono text-lg tracking-[0.4em]"
          autoFocus
          data-testid="mfa-totp-code-input"
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isConfirming || code.trim().length < 6}
        data-testid="mfa-totp-confirm-button"
      >
        {isConfirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('mfa.totpSetup.verify')}
      </Button>
    </form>
  );
}
